import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { type GrantEntry, type RoleOption } from "../../../shared/rbac/roles";
import type { SpaceMode } from "../../../shared/schema";
import { useGroupMemberships } from "../../hooks/useGroupMemberships";
import { useMouseDownOutside } from "../../hooks/useMouseDownOutside";
import { countSpaceMembers } from "../../lib/countSpaceMembers";
import { memberCountLabel } from "../../lib/memberGroupMap";
import { type ResolveScopeName, roleIdFromChipKey } from "../../lib/roleChip";
import { playClick } from "../../lib/sounds";
import { spaceIcon } from "../../lib/spaceIcon";
import { Button } from "../Button";
import { ConnectorLogo } from "../ConnectorLogo";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { Dropdown } from "../Dropdown";
import { openRoleModal } from "../globalModalStack";
import { Icon } from "../Icon";
import { RedactedScopeRow } from "../RedactedScopeRow";
import { RolePills } from "../RolePills";
import type { ScopeAccessPendingHandle } from "../ScopeAccessTable";
import { MetaSep } from "../MetaSeparator";
import { Text } from "../Text";
import { type EditableScopeKind as ManageableKind } from "./lib/inferScopeKind";
import { OverflowBadges } from "./OverflowBadges";
import {
  buildScopeDesired,
  type AccessPrincipal,
} from "./lib/buildScopeDesired";
import { SelectableMatchIcon } from "../SelectableMatchIcon";
import { useShiftHeld } from "../../hooks/useShiftHeld";
import {
  matchesInRange,
  sameRoleOption as sameRole,
} from "../../lib/grantAssembly";
import {
  AnchoredListPopover,
  ClickablePillBadge,
  DetailLabel,
  DetailList,
  DetailRow,
  DetailRowRight,
  MatchRow,
  MatchSectionLabel,
  NeutralIconBadge,
  PlainInput,
  SearchBar,
  SearchIconWrap,
  SearchInputWrap,
} from "./shared";

import {
  buildScopeIndex,
  buildScopeAccessRows,
  findScopeMatches,
  pendingScopeSelections,
  stageScopeMatches,
  matchKey,
  type PrincipalGrant,
  type InheritedGrant,
  type SpaceRow,
  type AppRow,
  type SkillRow,
  type CustomRole,
  type Row,
  type StagedScope,
  type Match,
} from "./lib/scopeAccess";

const Root = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

const SelectedHeader = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const MatchDivider = styled("div")`
  height: 0.5px;
  margin: 12px 8px 4px;
  background: var(--border-alpha);
`;

function ScopeIcon({
  scopeKind,
  mode,
  name,
  identifier,
}: {
  scopeKind: ManageableKind;
  mode?: SpaceMode;
  name: string;
  identifier?: string;
}) {
  if (scopeKind === "connected_app" && identifier) {
    return (
      <ConnectorLogoContainer
        size={32}
        borderRadius={8}
        outline={false}
        hoverBackground={false}
      >
        <ConnectorLogo template={{ name, identifier }} size={16} />
      </ConnectorLogoContainer>
    );
  }
  if (scopeKind === "skill") return <NeutralIconBadge iconName="skills" />;
  return <NeutralIconBadge iconName={mode ? spaceIcon(mode) : "hash"} />;
}

const SCOPE_LABELS: Record<ManageableKind, string> = {
  space: "Space",
  connected_app: "Connector",
  skill: "Skill",
};

function viaLabel(viaGroups: readonly { name: string }[]): string {
  if (viaGroups.length === 1) return `via ${viaGroups[0]!.name}`;
  return `via ${viaGroups.length} groups`;
}

export function ResourceAccessTable({
  principal,
  isSelf,
  directGrants,
  inheritedGrants = [],
  spaces,
  apps,
  skills,
  customRoles,
  resolveScopeName,
  personalSpaceId,
  manageableSpaceIds,
  manageableAppIds,
  manageableSkillIds,
  canManage,
  stagedGrantsRef,
  hasTenantRead,
  onOpenResource,
  commitScope,
  onRequestSelfRemove,
  onRemoveConnectorAccess,
  hideEmpty = false,
  busy = false,
  selectionReady = true,
  isRemovalReady = () => true,
}: {
  hideEmpty?: boolean;
  busy?: boolean;
  selectionReady?: boolean;
  isRemovalReady?: (scopeKind: ManageableKind) => boolean;
  principal: AccessPrincipal;
  isSelf: boolean;
  directGrants: readonly PrincipalGrant[];
  inheritedGrants?: readonly InheritedGrant[];
  spaces: readonly SpaceRow[];
  apps: readonly AppRow[];
  skills: readonly SkillRow[];
  customRoles: readonly CustomRole[];
  resolveScopeName: ResolveScopeName;
  personalSpaceId?: string;
  manageableSpaceIds: ReadonlySet<string>;
  manageableAppIds: ReadonlySet<string>;
  manageableSkillIds: ReadonlySet<string>;
  canManage: boolean;
  stagedGrantsRef?: { current: ScopeAccessPendingHandle | null };
  hasTenantRead: Record<ManageableKind, boolean>;
  onOpenResource: (scopeId: string, scopeKind: ManageableKind) => void;
  commitScope: (
    scopeId: string,
    scopeKind: ManageableKind,
    desired: GrantEntry[],
    onCommitted?: () => void,
    onBlocked?: () => void
  ) => boolean | void | Promise<boolean | void>;
  onRequestSelfRemove: (
    scopeId: string,
    scopeKind: ManageableKind,
    desired: GrantEntry[]
  ) => void;
  onRemoveConnectorAccess: (appId: string, desired: GrantEntry[]) => void;
}) {
  const canSelect = canManage && selectionReady && !busy;
  const { memberUserIds } = useGroupMemberships();

  const [search, setSearch] = useState("");
  const [showMatches, setShowMatches] = useState(false);
  const [staged, setStaged] = useState<StagedScope[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const [searchBarRef, popoverRef] = useMouseDownOutside(() =>
    setShowMatches(false)
  );
  const inputRef = useRef<HTMLInputElement | null>(null);
  const matchRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const scrollHighlightIntoView = useRef(false);
  const [anchorKey, setAnchorKey] = useState<string | null>(null);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const shiftHeld = useShiftHeld(showMatches);

  const scopeById = buildScopeIndex({ spaces, apps, skills });
  // Pruned during render, so a scope never shows as both staged and committed.
  const filteredStaged = pendingScopeSelections(staged, scopeById, principal);
  if (filteredStaged.length !== staged.length) setStaged(filteredStaged);

  const { rows, hidden } = buildScopeAccessRows({
    principal,
    directGrants,
    inheritedGrants,
    scopeById,
    customRoles,
    resolveScopeName,
    personalSpaceId,
    canManage,
    manageableSpaceIds,
    manageableAppIds,
    manageableSkillIds,
  });

  const visibleHidden = {
    space: hasTenantRead.space ? 0 : hidden.space,
    connected_app: hasTenantRead.connected_app ? 0 : hidden.connected_app,
    skill: hasTenantRead.skill ? 0 : hidden.skill,
  };

  const stagedKeys = new Set(filteredStaged.map((s) => s.key));

  const { spaceMatches, appMatches, skillMatches, flatMatches } =
    findScopeMatches({
      spaces,
      apps,
      skills,
      search,
      directScopeIds: new Set(directGrants.map((g) => g.scopeId)),
      personalSpaceId,
      manageableSpaceIds,
      manageableAppIds,
      manageableSkillIds,
    });

  const hasMatches = flatMatches.length > 0;

  const resetHighlightForSearch = (value: string) => {
    setSearch(value);
    scrollHighlightIntoView.current = true;
    setAnchorKey(null);
    setHighlightedIndex(0);
  };

  useEffect(() => {
    if (!showMatches || !scrollHighlightIntoView.current) return;
    matchRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, showMatches]);

  const previewKeys = new Set(
    (shiftHeld
      ? matchesInRange(flatMatches, matchKey, anchorKey, hoveredKey)
      : []
    ).map(matchKey)
  );

  const stageMatches = (matches: Match[]) => {
    const additions = stageScopeMatches(matches, scopeById, customRoles);
    setStaged((prev) => [
      ...prev,
      ...additions.filter((item) => !prev.some((s) => s.key === item.key)),
    ]);
  };

  const selectMatch = (match: Match) => {
    playClick();
    stageMatches([match]);
    resetHighlightForSearch("");
    setShowMatches(false);
    inputRef.current?.blur();
  };

  const toggleStageMatch = (match: Match, shiftKey: boolean) => {
    playClick();
    if (shiftKey && anchorKey) {
      const range = matchesInRange(
        flatMatches,
        matchKey,
        anchorKey,
        matchKey(match)
      );
      if (range.length > 0) {
        stageMatches(range);
        return;
      }
    }
    const key = matchKey(match);
    if (stagedKeys.has(key))
      setStaged((prev) => prev.filter((s) => s.key !== key));
    else stageMatches([match]);
    setAnchorKey(key);
  };

  const toggleStagedRole = (key: string, opt: RoleOption) => {
    setStaged((prev) =>
      prev.map((s) => {
        if (s.key !== key) return s;
        const has = s.roles.some((r) => sameRole(r, opt));
        if (has) {
          if (s.roles.length <= 1) return s;
          return { ...s, roles: s.roles.filter((r) => !sameRole(r, opt)) };
        }
        return { ...s, roles: [...s.roles, opt] };
      })
    );
  };

  const removeStaged = (key: string) => {
    setStaged((prev) => prev.filter((s) => s.key !== key));
  };

  const rolesSelected = filteredStaged.every((s) => s.roles.length > 0);
  const canCommit = canSelect && filteredStaged.length > 0 && rolesSelected;

  const commitAll = (onCommitted?: () => void) => {
    if (!canCommit) return;
    resetHighlightForSearch("");
    setShowMatches(false);
    const commitNext = (index: number, allCommitted: boolean) => {
      const item = filteredStaged[index];
      if (!item) {
        if (allCommitted) onCommitted?.();
        return;
      }
      const scope = scopeById.get(item.scopeId);
      if (!scope) {
        commitNext(index + 1, false);
        return;
      }
      const proceed = () => {
        setStaged((prev) => prev.filter((s) => s.key !== item.key));
        commitNext(index + 1, allCommitted);
      };
      const result = commitScope(
        item.scopeId,
        item.scopeKind,
        buildScopeDesired(scope.grants, principal, item.roles),
        proceed,
        () => commitNext(index + 1, false)
      );
      if (result instanceof Promise) {
        void result.then((committed) => {
          if (committed !== false) proceed();
        });
      } else if (result !== false) proceed();
    };
    commitNext(0, true);
  };

  useEffect(() => {
    if (!stagedGrantsRef) return;
    stagedGrantsRef.current = {
      pendingCount: filteredStaged.length,
      canCommit,
      blockedReason: !canManage
        ? "You don’t have permission to change access."
        : !rolesSelected
          ? "Choose a role for each to add them."
          : undefined,
      commitPending: commitAll,
    };
    return () => {
      stagedGrantsRef.current = null;
    };
  });

  const toggleRow = (row: Row, opt: RoleOption) => {
    if (!canSelect) return;
    const current: RoleOption[] = row.directGrants.map((g) => ({
      builtinRoleId: g.builtinRoleId as RoleOption["builtinRoleId"],
      customRoleId: g.customRoleId,
      label: "",
    }));
    const has = current.some((r) => sameRole(r, opt));
    if (has && current.length <= 1) return;
    const next = has
      ? current.filter((r) => !sameRole(r, opt))
      : [...current, opt];
    const scope = scopeById.get(row.scopeId);
    if (!scope) return;
    commitScope(
      row.scopeId,
      row.scopeKind,
      buildScopeDesired(scope.grants, principal, next)
    );
  };

  const removeRow = (row: Row) => {
    const scope = scopeById.get(row.scopeId);
    if (!scope) return;
    const desired = buildScopeDesired(scope.grants, principal, []);
    if (busy || !isRemovalReady(row.scopeKind)) return;
    if (row.scopeKind === "connected_app")
      onRemoveConnectorAccess(row.scopeId, desired);
    else if (isSelf) onRequestSelfRemove(row.scopeId, row.scopeKind, desired);
    else commitScope(row.scopeId, row.scopeKind, desired);
  };

  const isEmpty =
    rows.length === 0 &&
    filteredStaged.length === 0 &&
    visibleHidden.space === 0 &&
    visibleHidden.connected_app === 0 &&
    visibleHidden.skill === 0;

  return (
    <Root>
      {canManage && (
        <SearchBar
          ref={searchBarRef}
          style={{ opacity: canSelect ? 1 : 0.5 }}
          onMouseDown={(e) => {
            const target = e.target as HTMLElement;
            if (target.closest("button, input, [data-no-focus-steal]")) return;
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <SearchInputWrap>
            <SearchIconWrap>
              <Icon name="search" size={16} />
            </SearchIconWrap>
            <PlainInput
              ref={inputRef}
              disabled={!canSelect}
              placeholder="Add a space, connector, or skill…"
              value={search}
              onChange={(e) => {
                resetHighlightForSearch(e.target.value);
                setShowMatches(true);
              }}
              onFocus={() => setShowMatches(true)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setShowMatches(false);
                  resetHighlightForSearch("");
                } else if (e.key === "ArrowDown" && hasMatches) {
                  e.preventDefault();
                  setShowMatches(true);
                  scrollHighlightIntoView.current = true;
                  setHighlightedIndex((i) =>
                    Math.min(i + 1, flatMatches.length - 1)
                  );
                } else if (e.key === "ArrowUp" && hasMatches) {
                  e.preventDefault();
                  setShowMatches(true);
                  scrollHighlightIntoView.current = true;
                  setHighlightedIndex((i) => Math.max(i - 1, 0));
                } else if (e.key === "Enter" && hasMatches && showMatches) {
                  e.preventDefault();
                  const match = flatMatches[highlightedIndex];
                  if (match) selectMatch(match);
                }
              }}
            />
          </SearchInputWrap>
          <AnchoredListPopover
            anchorRef={searchBarRef}
            open={canSelect && showMatches && hasMatches}
            popoverRef={popoverRef}
          >
            {(
              [
                {
                  kind: "space",
                  label: "Spaces",
                  matches: spaceMatches,
                  offset: 0,
                },
                {
                  kind: "connected_app",
                  label: "Connectors",
                  matches: appMatches,
                  offset: spaceMatches.length,
                },
                {
                  kind: "skill",
                  label: "Skills",
                  matches: skillMatches,
                  offset: spaceMatches.length + appMatches.length,
                },
              ] as const
            ).map(
              ({ kind, label, matches, offset }) =>
                matches.length > 0 && (
                  <div key={kind}>
                    {offset > 0 && <MatchDivider />}
                    <MatchSectionLabel>
                      <Text variant="text-small" color="var(--text-secondary)">
                        {label}
                      </Text>
                    </MatchSectionLabel>
                    {matches.map((resource, i) => {
                      const match = { scopeId: resource.id, scopeKind: kind };
                      const key = matchKey(match);
                      const index = offset + i;
                      return (
                        <MatchRow
                          key={key}
                          ref={(el) => {
                            matchRefs.current[index] = el;
                          }}
                          type="button"
                          data-highlighted={highlightedIndex === index}
                          onMouseEnter={() => {
                            scrollHighlightIntoView.current = false;
                            setHighlightedIndex(index);
                            setHoveredKey(key);
                          }}
                          onMouseLeave={() =>
                            setHoveredKey((current) =>
                              current === key ? null : current
                            )
                          }
                          onMouseDown={(e) => {
                            e.preventDefault();
                            if (e.shiftKey) toggleStageMatch(match, true);
                            else selectMatch(match);
                          }}
                        >
                          <SelectableMatchIcon
                            staged={stagedKeys.has(key)}
                            preview={previewKeys.has(key)}
                            onToggle={(shiftKey) =>
                              toggleStageMatch(match, shiftKey)
                            }
                          >
                            {"identifier" in resource ? (
                              <ConnectorLogo
                                template={{
                                  name: resource.name,
                                  identifier: resource.identifier,
                                }}
                                size={16}
                              />
                            ) : (
                              <Icon
                                name={
                                  "mode" in resource
                                    ? spaceIcon(resource.mode)
                                    : "skills"
                                }
                                size={16}
                              />
                            )}
                          </SelectableMatchIcon>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text
                              variant="text-regular"
                              color="var(--text-primary)"
                              ellipsis
                            >
                              {resource.name}
                            </Text>
                            <Text
                              variant="text-small"
                              color="var(--text-secondary)"
                              ellipsis
                            >
                              {memberCountLabel(
                                countSpaceMembers(
                                  resource.roleGrantsAsScope,
                                  memberUserIds
                                )
                              )}
                            </Text>
                          </div>
                        </MatchRow>
                      );
                    })}
                  </div>
                )
            )}
          </AnchoredListPopover>
        </SearchBar>
      )}

      {filteredStaged.length > 0 && (
        <DetailList>
          <SelectedHeader>
            <Text variant="text-regular-md" color="var(--text-primary)">
              Selected
            </Text>
            <Button
              variant="primary"
              disabled={!canCommit}
              onClick={() => commitAll()}
            >
              Add {filteredStaged.length}
            </Button>
          </SelectedHeader>
          {filteredStaged.map((s) => (
            <DetailRow key={s.key}>
              <ScopeIcon
                scopeKind={s.scopeKind}
                mode={s.mode}
                name={s.name}
                identifier={s.identifier}
              />
              <DetailLabel>
                <Text
                  variant="text-regular-md"
                  color="var(--text-primary)"
                  ellipsis
                >
                  {s.name}
                </Text>
                <Text variant="text-small" color="var(--text-secondary)">
                  {scopeById.has(s.scopeId)
                    ? SCOPE_LABELS[s.scopeKind]
                    : "This resource is no longer available."}
                </Text>
              </DetailLabel>
              <DetailRowRight>
                <RolePills
                  roles={s.roles.map((r) => r.label).sort()}
                  grants={s.roles.map((r) => ({
                    builtinRoleId: r.builtinRoleId,
                    customRoleId: r.customRoleId,
                  }))}
                  roleOptions={s.roleOptions}
                  canManage={canSelect}
                  onToggle={(opt) => toggleStagedRole(s.key, opt)}
                />
                <Button
                  variant="secondary"
                  icon="x"
                  disabled={!canSelect}
                  onClick={() => removeStaged(s.key)}
                />
              </DetailRowRight>
            </DetailRow>
          ))}
        </DetailList>
      )}

      {!(hideEmpty && isEmpty) && (
        <DetailList>
          {isEmpty ? (
            <DetailRow>
              <NeutralIconBadge iconName="circle-slash" />
              <DetailLabel>
                <Text variant="text-regular-md">No access</Text>
                <Text variant="text-small" color="var(--text-secondary)">
                  {principal.userGroupId != null
                    ? "Access appears here when this group is granted a role."
                    : principal.serviceAccountId != null
                      ? "Access appears here when this service account is granted a role."
                      : "Access appears here when this member or one of their groups is granted a role."}
                </Text>
              </DetailLabel>
            </DetailRow>
          ) : (
            <>
              {rows.map((row) => (
                <DetailRow
                  key={row.scopeId}
                  data-interactive="true"
                  onMouseDown={() => playClick()}
                  onClick={() => onOpenResource(row.scopeId, row.scopeKind)}
                >
                  <ScopeIcon
                    scopeKind={row.scopeKind}
                    mode={row.mode}
                    name={row.name}
                    identifier={row.identifier}
                  />
                  <DetailLabel>
                    <Text
                      variant="text-regular-md"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {row.name}
                    </Text>
                    <Text variant="text-small" color="var(--text-secondary)">
                      {SCOPE_LABELS[row.scopeKind]}
                      {row.viaGroups.length > 0 && (
                        <>
                          <MetaSep />
                          {viaLabel(row.viaGroups)}
                        </>
                      )}
                    </Text>
                  </DetailLabel>
                  <DetailRowRight>
                    {row.directGrants.length > 0 ? (
                      <>
                        <RolePills
                          roles={row.directRoleLabels}
                          grants={row.directGrants}
                          roleOptions={row.roleOptions}
                          canManage={row.editable && canSelect}
                          onToggle={(opt) => toggleRow(row, opt)}
                        />
                        {row.editable && (
                          <div
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Dropdown
                              opener={
                                <Button
                                  variant="secondary"
                                  icon="more"
                                  disabled={
                                    busy || !isRemovalReady(row.scopeKind)
                                  }
                                />
                              }
                              align="right"
                              minWidth={160}
                              sections={[
                                {
                                  items: [
                                    {
                                      type: "button" as const,
                                      left: <Icon name="trash" size={16} />,
                                      label: "Remove access",
                                      disabled:
                                        busy || !isRemovalReady(row.scopeKind),
                                      onClick: () => removeRow(row),
                                    },
                                  ],
                                },
                              ]}
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <OverflowBadges
                        items={row.inheritedChips}
                        keyFn={(chip) => chip.key}
                        renderBadge={(chip) => (
                          <ClickablePillBadge
                            icon={chip.icon}
                            onClick={() => {
                              playClick();
                              openRoleModal(roleIdFromChipKey(chip.key));
                            }}
                          >
                            {chip.label}
                          </ClickablePillBadge>
                        )}
                        overflowTooltip={(h) =>
                          h.map((c) => c.label).join(", ")
                        }
                      />
                    )}
                  </DetailRowRight>
                </DetailRow>
              ))}
              <RedactedScopeRow count={visibleHidden.space} scopeKind="space" />
              <RedactedScopeRow
                count={visibleHidden.connected_app}
                scopeKind="connected_app"
              />
              <RedactedScopeRow count={visibleHidden.skill} scopeKind="skill" />
            </>
          )}
        </DetailList>
      )}
    </Root>
  );
}
